<template>
    <b-card no-body class="py-1">
        <b-overlay :show="getting" opacity="0.2">
            <div class="d-flex flex-wrap align-items-center justify-content-between px-1 mb-1">
                <h5 class="mb-0">Fiyat Değişim Geçmişi</h5>
                <b-button size="sm" variant="success" :disabled="downloading || getting" @click="downloadExcel">
                    <feather-icon icon="DownloadIcon" class="mr-50" />
                    {{ downloading ? 'Hazırlanıyor...' : "Excel'e Aktar" }}
                </b-button>
            </div>

            <div class="table-responsive px-1">
                <table class="table table-bordered table-sm align-middle mb-1">
                    <thead>
                        <tr>
                            <th class="text-nowrap">Kod</th>
                            <th class="text-nowrap">Cari</th>
                            <th class="text-nowrap">{{ isOutAgreement ? 'Dış Anlaşma Kodu' : 'Sözleşme Kodu' }}</th>
                            <th class="text-nowrap">{{ isOutAgreement ? 'Dış Anlaşma Adı' : 'Sözleşme Adı' }}</th>
                            <th v-if="!isOutAgreement" class="text-nowrap">Sözleşme Baş. Tarihi</th>
                            <th v-if="!isOutAgreement" class="text-nowrap">Sözleşme Bit. Tarihi</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td class="text-nowrap">{{ agreement.CariCode || '-' }}</td>
                            <td class="text-nowrap">{{ agreement.CariName || '-' }}</td>
                            <td class="text-nowrap">{{ agreement.AgreementCode || '-' }}</td>
                            <td class="text-nowrap">{{ agreement.AgreementName || '-' }}</td>
                            <td v-if="!isOutAgreement" class="text-nowrap">{{ agreement.StartDate || '-' }}</td>
                            <td v-if="!isOutAgreement" class="text-nowrap">{{ agreement.ExpireDate || '-' }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Fiyat parametreleri: "mazot %24 arttı ama fiyat neden %16 arttı" sorusunun cevabı -->
            <div v-if="currentFactor" class="px-1 mb-1">
                <div class="d-flex flex-wrap align-items-center">
                    <span class="mr-1 font-weight-bolder">Fiyat Parametreleri:</span>
                    <b-badge variant="light-secondary" class="mr-50 mb-25">Yakıt Payı: {{ currentFactor.FuelAffectValueStr }}</b-badge>
                    <b-badge variant="light-secondary" class="mr-50 mb-25">Mazot Eşiği: {{ currentFactor.FuelAffectRateValueStr }}</b-badge>
                    <b-badge variant="light-secondary" class="mr-50 mb-25">Enflasyon Payı: {{ currentFactor.InflationAffectValueStr }}</b-badge>
                    <b-badge variant="light-secondary" class="mr-50 mb-25">Enflasyon Oranı: {{ currentFactor.InflationAffectRateValueStr }}</b-badge>
                    <b-button
                        v-if="(agreement.PriceFactors || []).length > 1"
                        size="sm"
                        variant="flat-primary"
                        class="py-25"
                        @click="showFactorHistory = !showFactorHistory">
                        {{ showFactorHistory ? 'Geçmişi Gizle' : `Parametre Geçmişi (${agreement.PriceFactors.length})` }}
                    </b-button>
                </div>
                <div v-if="showFactorHistory" class="table-responsive mt-1">
                    <table class="table table-bordered table-sm mb-0">
                        <thead>
                            <tr>
                                <th class="text-nowrap">Geçerlilik Tarihi</th>
                                <th class="text-nowrap">Yakıt Payı</th>
                                <th class="text-nowrap">Mazot Eşiği</th>
                                <th class="text-nowrap">Enflasyon Payı</th>
                                <th class="text-nowrap">Enflasyon Oranı</th>
                                <th class="text-nowrap">Durum</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(f,i) in agreement.PriceFactors" :key="'f'+i">
                                <td class="text-nowrap">{{ f.DateStr }}</td>
                                <td class="text-nowrap">{{ f.FuelAffectValueStr }}</td>
                                <td class="text-nowrap">{{ f.FuelAffectRateValueStr }}</td>
                                <td class="text-nowrap">{{ f.InflationAffectValueStr }}</td>
                                <td class="text-nowrap">{{ f.InflationAffectRateValueStr }}</td>
                                <td class="text-nowrap">
                                    <b-badge v-if="f.IsCurrent" variant="success">Güncel</b-badge>
                                    <span v-else class="text-muted">Geçmiş</span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <!-- Mazot - ortalama rota fiyatı karşılaştırması -->
            <div v-if="hasChart" class="px-1 mb-1">
                <div class="d-flex flex-wrap align-items-center justify-content-between">
                    <span class="font-weight-bolder">Mazot / Ortalama Rota Fiyatı</span>
                    <div>
                        <b-button
                            size="sm"
                            :variant="chartMode === 'index' ? 'primary' : 'outline-primary'"
                            class="py-25 mr-50"
                            @click="chartMode = 'index'">Endeks (100)</b-button>
                        <b-button
                            size="sm"
                            :variant="chartMode === 'value' ? 'primary' : 'outline-primary'"
                            class="py-25 mr-50"
                            @click="chartMode = 'value'">TL</b-button>
                        <b-button size="sm" variant="flat-secondary" class="py-25" @click="showChart = !showChart">
                            {{ showChart ? 'Gizle' : 'Göster' }}
                        </b-button>
                    </div>
                </div>
                <vue-apex-charts
                    v-if="showChart"
                    :key="chartMode"
                    type="line"
                    height="290"
                    :options="chartOptions"
                    :series="chartSeries" />
                <div v-if="showChart" class="pch-legend-box">
                    <div class="d-flex flex-wrap">
                        <div class="mr-2 mb-25">
                            <span class="pch-swatch" style="background:#ff9f43"></span>
                            <b>Mazot</b> — {{ chartMode === 'index' ? 'mazot fiyatının endeksi' : 'mazot fiyatı (₺, sol eksen)' }}
                        </div>
                        <div class="mb-25">
                            <span class="pch-swatch" style="background:#7367f0"></span>
                            <b>Ortalama Rota Fiyatı</b> — {{ chartMode === 'index' ? 'geçerli rotaların ortalama fiyatının endeksi' : 'geçerli rotaların ortalama fiyatı (₺, sağ eksen)' }}
                        </div>
                    </div>
                    <div v-if="chartMode === 'index'" class="mt-25">
                        <b>Endeks nedir?</b> Her iki seri de ilk tarihteki değeri <b>100</b> kabul edilerek yeniden ölçeklenir:
                        <code>endeks = (o günkü değer ÷ ilk günkü değer) × 100</code>.
                        Mazot ile rota fiyatı çok farklı büyüklüklerde olduğu için ({{ firstFuelLabel }} ve {{ firstPriceLabel }})
                        aynı grafikte doğrudan karşılaştırılamaz; endeksleyince ikisi de aynı noktadan başlar ve
                        <b>yüzde değişimleri</b> karşılaştırılabilir hale gelir.
                        <br />
                        Örnek: mazot endeksi <b>152</b> ise mazot ilk tarihe göre <b>%52</b> artmış demektir.
                        <b>İki eğri arasındaki açıklık, mazot artışının fiyata yansımayan kısmıdır</b> — mor çizgi turuncunun altındaysa
                        zam mazottan daha yavaş ilerlemiştir.
                    </div>
                    <div v-else class="mt-25">
                        Gerçek TL değerleri gösterilir. İki serinin büyüklükleri farklı olduğu için ayrı eksen kullanılır;
                        eğimleri kıyaslamak için <b>Endeks</b> moduna geçin.
                    </div>
                </div>
            </div>

            <!-- Filtreler -->
            <div class="d-flex flex-wrap align-items-end px-1 mb-1">
                <div class="mr-1 mb-50" style="min-width:220px">
                    <label class="small mb-25">Rota Ara</label>
                    <b-form-input v-model="search" size="sm" placeholder="Bölge, araç tipi, stok..." />
                </div>
                <div class="mr-1 mb-50" style="min-width:150px">
                    <label class="small mb-25">Başlangıç Tarihi</label>
                    <v-date-picker v-model="dateFrom" input-class="form-control form-control-sm" />
                </div>
                <div class="mr-1 mb-50" style="min-width:150px">
                    <label class="small mb-25">Bitiş Tarihi</label>
                    <v-date-picker v-model="dateTo" input-class="form-control form-control-sm" />
                </div>
                <div class="mr-1 mb-50">
                    <b-form-checkbox v-model="onlyActiveRoutes" switch>Sadece aktif rotalar</b-form-checkbox>
                </div>
                <div class="mb-50">
                    <b-button size="sm" variant="flat-secondary" class="py-25" @click="clearFilters">Filtreleri Temizle</b-button>
                </div>
            </div>

            <div class="table-responsive mb-0">
                <table class="table table-sm table-bordered pch-table">
                    <thead>
                        <tr>
                            <th class="text-nowrap pch-sticky pch-col-0">Sıra</th>
                            <th class="text-nowrap pch-sticky pch-col-1">Başlangıç Bölgesi</th>
                            <th class="text-nowrap pch-sticky pch-col-2">Bitiş Bölgesi</th>
                            <th class="text-nowrap">Araç Tipi</th>
                            <th class="text-nowrap">Stok Kodu</th>
                            <th class="text-nowrap">Ürün Tipi</th>
                            <th class="text-nowrap">Taşıma Tipi</th>
                            <th
                                class="text-nowrap text-center pch-head"
                                v-for="c in columns"
                                :key="c.key"
                                v-b-tooltip.hover.html
                                :title="c.deviationNote">
                                <div>
                                    {{ c.key }}
                                    <span
                                        v-if="c.hasDeviation"
                                        class="material-icons-outlined pch-warn-icon">warning_amber</span>
                                </div>
                                <div v-if="c.changeType" class="mt-25">
                                    <b-badge :variant="changeVariant(c.changeType)">{{ c.changeType }}</b-badge>
                                </div>
                                <div v-if="c.fuelPrice" class="mt-25 small text-muted font-weight-normal">
                                    Mazot: {{ c.fuelPrice }} ₺
                                    <span v-if="c.fuelChangeRate">({{ c.fuelChangeRate }})</span>
                                </div>
                                <div v-if="c.priceChangeRate" class="small font-weight-normal" :class="rateClass(c.priceChangeRate)">
                                    Fiyat: {{ c.priceChangeRate }}
                                </div>
                                <div v-if="c.expectedChangeRate" class="small font-weight-normal text-muted">
                                    Beklenen: {{ c.expectedChangeRate }}
                                </div>
                                <div v-if="c.deviationStr" class="small font-weight-normal" :class="c.hasDeviation ? 'text-danger' : 'text-success'">
                                    {{ c.hasDeviation ? 'Sapma: ' + c.deviationStr : 'Sapma yok' }}
                                </div>
                            </th>
                            <th class="text-nowrap text-center bg-light">İlk Fiyat</th>
                            <th class="text-nowrap text-center bg-light">Son Fiyat</th>
                            <th class="text-nowrap text-center bg-light">Toplam Değişim</th>
                            <th class="text-nowrap text-center bg-light">Mazot Değişimi</th>
                            <th class="text-nowrap text-center bg-light">Yansıtma Oranı</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(d,i) in routes" :key="'r'+i">
                            <td class="text-nowrap text-center font-weight-bolder pch-sticky pch-col-0">{{ i+1 }}</td>
                            <td class="text-nowrap pch-sticky pch-col-1">{{ d.StartRegion }}</td>
                            <td class="text-nowrap pch-sticky pch-col-2">{{ d.EndRegion }}</td>
                            <td class="text-nowrap">{{ d.VehicleType }}</td>
                            <td class="text-nowrap">{{ d.StockCode }}</td>
                            <td class="text-nowrap">{{ d.StockName }}</td>
                            <td class="text-nowrap">
                                {{ d.Status }}
                                <b-badge v-if="!d.IsActive" variant="secondary" class="ml-25">Sonlandı</b-badge>
                            </td>
                            <td
                                v-for="c in columns"
                                :key="c.key"
                                class="text-nowrap text-right"
                                :class="cellClass(cellOf(d,c))">
                                <template v-if="cellOf(d,c).IsActive && cellOf(d,c).PriceStr">
                                    <span
                                        v-b-tooltip.hover
                                        :title="cellOf(d,c).Note"
                                        :class="cellOf(d,c).IsChange ? 'font-weight-bolder' : 'text-muted font-italic'">
                                        {{ cellOf(d,c).PriceStr }}
                                    </span>
                                    <span
                                        v-if="cellOf(d,c).IsManuelChanged"
                                        v-b-tooltip.hover
                                        :title="`Manuel değiştirildi. Önceki fiyat: ${cellOf(d,c).ManuelOldPriceStr || '-'}`"
                                        class="material-icons-outlined pch-manual-icon">edit</span>
                                    <span
                                        v-else-if="cellOf(d,c).IsDeviated"
                                        v-b-tooltip.hover
                                        :title="cellOf(d,c).Note"
                                        class="material-icons-outlined pch-warn-icon">warning_amber</span>
                                    <div
                                        v-if="cellOf(d,c).IsChange && cellOf(d,c).ChangeRateStr"
                                        class="small"
                                        :class="rateClass(cellOf(d,c).ChangeRateStr)">
                                        {{ cellOf(d,c).ChangeRateStr }}
                                    </div>
                                </template>
                            </td>
                            <td class="text-nowrap text-right bg-light">{{ d.FirstPriceStr || '-' }}</td>
                            <td class="text-nowrap text-right bg-light">{{ d.LastPriceStr || '-' }}</td>
                            <td class="text-nowrap text-right bg-light font-weight-bolder" :class="rateClass(d.TotalChangeRateStr)">
                                {{ d.TotalChangeRateStr || '-' }}
                            </td>
                            <td class="text-nowrap text-right bg-light">{{ d.FuelTotalChangeRateStr || '-' }}</td>
                            <td class="text-nowrap text-right bg-light">{{ d.ReflectionRateStr || '-' }}</td>
                        </tr>
                        <tr v-if="routes.length == 0">
                            <td class="text-center" colspan="1000">Henüz bir veri bulunmamaktadır.</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="px-1 pt-50 small text-muted">
                <span class="font-weight-bolder text-dark mr-25">Kalın</span> o tarihte değişen fiyat
                <span class="mx-50">|</span>
                <span class="font-italic mr-25">Soluk italik</span> değişmeden devam eden fiyat
                <span class="mx-50">|</span>
                <span class="pch-legend-inactive mr-25"></span> rota o tarihte sözleşmede yok
            </div>

            <!-- Mazot değişmiş ama eşiğin altında kaldığı için zam yapılmayan günler -->
            <div v-if="(agreement.SkippedFuelChecks || []).length > 0" class="px-1 mt-1">
                <b-button size="sm" variant="flat-warning" class="py-25" @click="showSkipped = !showSkipped">
                    {{ showSkipped ? 'Gizle' : `Eşik Altında Kalan Mazot Kontrolleri (${agreement.SkippedFuelCheckTotalCount})` }}
                </b-button>
                <div v-if="showSkipped" class="table-responsive mt-1">
                    <table class="table table-bordered table-sm mb-0">
                        <thead>
                            <tr>
                                <th class="text-nowrap">Tarih</th>
                                <th class="text-nowrap">Önceki Mazot</th>
                                <th class="text-nowrap">Güncel Mazot</th>
                                <th class="text-nowrap">Mazot Değişimi</th>
                                <th class="text-nowrap">Eşik</th>
                                <th class="text-nowrap">Açıklama</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(s,i) in agreement.SkippedFuelChecks" :key="'s'+i">
                                <td class="text-nowrap">{{ s.DateStr }}</td>
                                <td class="text-nowrap text-right">{{ s.PreviousFuelPriceStr || '-' }}</td>
                                <td class="text-nowrap text-right">{{ s.CurrentFuelPriceStr || '-' }}</td>
                                <td class="text-nowrap text-right">{{ s.FuelChangeRateStr || '-' }}</td>
                                <td class="text-nowrap text-right">{{ s.ThresholdRateStr || '-' }}</td>
                                <td>{{ s.ResultMessage }}</td>
                            </tr>
                        </tbody>
                    </table>
                    <div v-if="agreement.SkippedFuelCheckTotalCount > agreement.SkippedFuelChecks.length" class="small text-muted mt-50">
                        Toplam {{ agreement.SkippedFuelCheckTotalCount }} kayıttan en güncel
                        {{ agreement.SkippedFuelChecks.length }} tanesi listelenmiştir.
                    </div>
                </div>
            </div>
        </b-overlay>
    </b-card>
</template>

<script>
import { AgreementApi } from '@/api/agreement'
import Additional from '@/utilities/additional'
import VDatePicker from '@/components/VDatePicker.vue'
import VueApexCharts from 'vue-apexcharts'
import { BBadge, BButton, BCard, BFormCheckbox, BFormInput, BOverlay, VBTooltip } from 'bootstrap-vue'

const EMPTY_CELL = { IsActive: false, IsChange: false, PriceStr: null }

export default {
    components: {
        BBadge,
        BButton,
        BCard,
        BFormCheckbox,
        BFormInput,
        BOverlay,
        VDatePicker,
        VueApexCharts
    },
    directives: {
        'b-tooltip': VBTooltip
    },
    props: {
        isOutAgreement: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            agreement: {},
            getting: true,
            downloading: false,
            search: '',
            dateFrom: null,
            dateTo: null,
            onlyActiveRoutes: false,
            showFactorHistory: false,
            showChart: true,
            chartMode: 'index',
            showSkipped: false
        }
    },
    mounted() {
        this.getHistory()
    },
    methods: {
        getHistory() {
            const id = this.$route.params.id
            AgreementApi.PriceChangeHistory(id).then(res => {
                if (res.data.Success) {
                    this.agreement = res.data.Data
                } else {
                    this.$result(res)
                }
                this.getting = false
            }).catch(err => {
                this.$result(null, err)
                this.getting = false
            })
        },
        downloadExcel() {
            const id = this.$route.params.id
            this.downloading = true
            AgreementApi.PriceChangeHistoryExcel(id).then(res => {
                const name = [this.agreement.CariCode, this.agreement.AgreementCode, this.agreement.AgreementName, 'Fiyat Değişim Geçmişi']
                    .filter(c => !!c).join(' - ').concat('.xlsx')
                Additional.DownloadFile(res.data, name)
                this.downloading = false
            }).catch(err => {
                this.$result(null, err)
                this.downloading = false
            })
        },
        clearFilters() {
            this.search = ''
            this.dateFrom = null
            this.dateTo = null
            this.onlyActiveRoutes = false
        },
        cellOf(route, column) {
            return (route.Cells || [])[column.index] || EMPTY_CELL
        },
        cellClass(cell) {
            // Rota o tarihte sözleşmede yoksa hücre taralı gösterilir; fiyatın değişmediği
            // hücreler ile "rota yok" hücreleri artık birbirine karışmıyor.
            return (!cell || !cell.IsActive) ? 'pch-cell-inactive' : null
        },
        rateClass(rateStr) {
            if (!rateStr) return null
            return rateStr.indexOf('-') > -1 ? 'text-danger' : 'text-success'
        },
        buildChartTooltip(index, isIndex, fuelPrices, averagePrices) {
            const chart = this.agreement.Chart || {}
            const column = (this.agreement.Changes || [])[index] || {}
            const label = (chart.Labels || [])[index] || ''
            const fuel = fuelPrices[index]
            const average = averagePrices[index]
            const fuelIndex = (chart.FuelIndex || [])[index]
            const priceIndex = (chart.PriceIndex || [])[index]

            const rows = []
            rows.push(`<div class="pch-tt-title">${label}${column.ChangeTypeStr ? ' · ' + column.ChangeTypeStr : ''}</div>`)
            rows.push(`<div><span class="pch-tt-dot" style="background:#ff9f43"></span>Mazot: <b>${fuel != null ? fuel + ' ₺' : '-'}</b>`
                + `${isIndex && fuelIndex != null ? ` <span class="pch-tt-muted">(endeks ${fuelIndex})</span>` : ''}`
                + `${column.FuelChangeRateStr ? ` <span class="pch-tt-muted">${column.FuelChangeRateStr}</span>` : ''}</div>`)
            rows.push(`<div><span class="pch-tt-dot" style="background:#7367f0"></span>Ortalama Rota Fiyatı: <b>${average != null ? average + ' ₺' : '-'}</b>`
                + `${isIndex && priceIndex != null ? ` <span class="pch-tt-muted">(endeks ${priceIndex})</span>` : ''}`
                + `${column.PriceChangeRateStr ? ` <span class="pch-tt-muted">${column.PriceChangeRateStr}</span>` : ''}</div>`)

            if (column.ExpectedChangeRateStr) {
                rows.push(`<div class="pch-tt-sep">Beklenen: <b>${column.ExpectedChangeRateStr}</b>`
                    + `${column.PriceChangeRateStr ? ` · Gerçekleşen: <b>${column.PriceChangeRateStr}</b>` : ''}</div>`)
                rows.push(column.HasDeviation
                    ? `<div class="pch-tt-bad">⚠ SAPMA: ${column.DeviationStr}</div>`
                    : '<div class="pch-tt-good">✓ Sapma yok</div>')
            }
            if (column.DeviationNote)
                rows.push(`<div class="pch-tt-note">${column.DeviationNote}</div>`)

            return `<div class="pch-tooltip">${rows.join('')}</div>`
        },
        changeVariant(changeType) {
            switch (changeType) {
                case 'Mazot Fiyatı': return 'warning'
                case 'Enflasyon': return 'info'
                case 'Sözleşme Başlangıç': return 'secondary'
                case 'Rota Eklendi': return 'success'
                case 'Manuel': return 'primary'
                default: return 'secondary'
            }
        }
    },
    computed: {
        currentFactor() {
            const factors = this.agreement.PriceFactors || []
            return factors.filter(f => f.IsCurrent).pop() || factors[factors.length - 1] || null
        },
        columns() {
            // Kolonların orijinal index'i korunuyor; hücrelere bu index üzerinden erişiliyor.
            return (this.agreement.Changes || [])
                .map((c, index) => ({
                    index,
                    key: c.DateStr,
                    date: (c.Date || '').substring(0, 10),
                    changeType: c.ChangeTypeStr,
                    fuelPrice: c.FuelPriceStr,
                    fuelChangeRate: c.FuelChangeRateStr,
                    priceChangeRate: c.PriceChangeRateStr,
                    expectedChangeRate: c.ExpectedChangeRateStr,
                    deviationStr: c.DeviationStr,
                    hasDeviation: c.HasDeviation,
                    deviationNote: c.DeviationNote
                }))
                .filter(c => {
                    if (this.dateFrom && c.date < this.dateFrom) return false
                    if (this.dateTo && c.date > this.dateTo) return false
                    return true
                })
        },
        routes() {
            const term = (this.search || '').trim().toLocaleLowerCase('tr')
            return (this.agreement.Routes || []).filter(r => {
                if (this.onlyActiveRoutes && !r.IsActive) return false
                if (!term) return true
                return [r.StartRegion, r.EndRegion, r.VehicleType, r.StockCode, r.StockName, r.Status]
                    .filter(v => !!v)
                    .some(v => v.toLocaleLowerCase('tr').indexOf(term) > -1)
            })
        },
        firstFuelLabel() {
            const v = ((this.agreement.Chart || {}).FuelPrices || []).find(x => x != null)
            return v != null ? `mazot ${v} ₺` : 'mazot fiyatı'
        },
        firstPriceLabel() {
            const v = ((this.agreement.Chart || {}).AveragePrices || []).find(x => x != null)
            return v != null ? `rota ${v} ₺` : 'rota fiyatı'
        },
        hasChart() {
            return ((this.agreement.Chart || {}).Labels || []).length > 1
        },
        chartSeries() {
            const chart = this.agreement.Chart || {}
            if (this.chartMode === 'value') {
                return [
                    { name: 'Mazot (₺)', data: chart.FuelPrices || [] },
                    { name: 'Ortalama Rota Fiyatı (₺)', data: chart.AveragePrices || [] }
                ]
            }
            return [
                { name: 'Mazot (endeks)', data: chart.FuelIndex || [] },
                { name: 'Ortalama Rota Fiyatı (endeks)', data: chart.PriceIndex || [] }
            ]
        },
        chartOptions() {
            const chart = this.agreement.Chart || {}
            const fuelPrices = chart.FuelPrices || []
            const averagePrices = chart.AveragePrices || []
            const isIndex = this.chartMode === 'index'

            // TL modunda iki serinin büyüklükleri çok farklı olduğu için ayrı eksen kullanılıyor.
            const yaxis = isIndex
                ? { title: { text: 'Endeks (ilk değer = 100)' } }
                : [
                    { seriesName: 'Mazot (₺)', title: { text: 'Mazot (₺)' }, labels: { formatter: v => (v == null ? '' : v.toFixed(2)) } },
                    { seriesName: 'Ortalama Rota Fiyatı (₺)', opposite: true, title: { text: 'Ortalama Rota Fiyatı (₺)' }, labels: { formatter: v => (v == null ? '' : v.toFixed(2)) } }
                ]

            return {
                chart: { toolbar: { show: false }, zoom: { enabled: false } },
                colors: ['#ff9f43', '#7367f0'],
                stroke: { width: 2, curve: 'straight' },
                markers: { size: 3 },
                dataLabels: { enabled: false },
                xaxis: { categories: chart.Labels || [] },
                yaxis,
                legend: { position: 'top' },
                tooltip: {
                    shared: true,
                    custom: ({ dataPointIndex }) => this.buildChartTooltip(dataPointIndex, isIndex, fuelPrices, averagePrices)
                }
            }
        }
    }
}
</script>

<style scoped>
.pch-table th.pch-sticky,
.pch-table td.pch-sticky {
    position: sticky;
    z-index: 2;
    background-color: #fff;
}

.pch-table thead th.pch-sticky {
    z-index: 3;
    background-color: #f3f2f7;
}

.pch-col-0 {
    left: 0;
    width: 48px;
    min-width: 48px;
}

.pch-col-1 {
    left: 48px;
    width: 160px;
    min-width: 160px;
}

.pch-col-2 {
    left: 208px;
    width: 160px;
    min-width: 160px;
}

.pch-cell-inactive {
    background-image: repeating-linear-gradient(45deg, #fafafa, #fafafa 4px, #efefef 4px, #efefef 8px);
}

.pch-legend-inactive {
    display: inline-block;
    width: 22px;
    height: 12px;
    vertical-align: middle;
    border: 1px solid #e0e0e0;
    background-image: repeating-linear-gradient(45deg, #fafafa, #fafafa 4px, #efefef 4px, #efefef 8px);
}

.pch-legend-box {
    font-size: 12px;
    line-height: 1.5;
    color: #6e6b7b;
    border: 1px solid #ebe9f1;
    border-radius: 6px;
    padding: 8px 10px;
    background-color: #fbfbfc;
}

.pch-swatch {
    display: inline-block;
    width: 12px;
    height: 12px;
    border-radius: 2px;
    margin-right: 4px;
    vertical-align: middle;
}

.pch-legend-box code {
    background-color: #f1f0f5;
    padding: 0 4px;
    border-radius: 3px;
}

.pch-warn-icon {
    font-size: 13px;
    vertical-align: middle;
    color: #ea5455;
    cursor: help;
}

.pch-head {
    cursor: help;
}

.pch-manual-icon {
    font-size: 12px;
    vertical-align: middle;
    color: #7367f0;
    cursor: help;
}
</style>

<style>
/* ApexCharts özel tooltip'i grafiğin kendi DOM'una basıldığı için scoped olamaz. */
.pch-tooltip {
    padding: 8px 10px;
    font-size: 12px;
    line-height: 1.6;
    max-width: 320px;
    white-space: normal;
}

.pch-tooltip .pch-tt-title {
    font-weight: 600;
    margin-bottom: 2px;
}

.pch-tooltip .pch-tt-dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    margin-right: 5px;
}

.pch-tooltip .pch-tt-muted {
    color: #b9b9c3;
}

.pch-tooltip .pch-tt-sep {
    margin-top: 4px;
    padding-top: 4px;
    border-top: 1px solid #ebe9f1;
}

.pch-tooltip .pch-tt-good {
    color: #28c76f;
    font-weight: 600;
}

.pch-tooltip .pch-tt-bad {
    color: #ea5455;
    font-weight: 600;
}

.pch-tooltip .pch-tt-note {
    margin-top: 3px;
    color: #6e6b7b;
    font-size: 11px;
}
</style>
